import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronUp, Pencil, X } from 'lucide-react'
import { apiClient } from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import {
  choicesSummary,
  draftFromGroup,
  draftProblem,
  emptyDraft,
  isRequired,
  isSizeGroup,
  kindOf,
  promptFor,
  ruleOf,
  rulePill,
  usageCounts,
  type VariantDraft,
} from '@/lib/variantShape'
import { matchTemplates, type VariantTemplate } from '@/lib/variantTemplates'
import {
  draftFromTemplate,
  newOwnSizes,
  nextTempKey,
  stateFromServer,
  type QuestionRow,
  type VariantsState,
} from '@/lib/productVariants'
import type { ModifierGroup } from '@/types'
import { VariantListEditor } from '@/components/admin/variants/VariantListEditor'
import { CashierPreview, type PreviewQuestion } from '@/components/admin/variants/CashierPreview'

/**
 * Sizes and questions, on the product form (F-MOD-N12).
 *
 * Two blocks the owner reads top to bottom:
 *
 *   Price      One price | By size   → the size list, typed in place
 *   Questions  what the cashier is asked, in order, with a type-to-add box
 *              that offers a shared list the menu already has, a template, or
 *              a brand-new question edited inline
 *
 * and the cashier's phone beside them. Sharing is something the form offers
 * ("Milk, used on 14 drinks"), never a step the owner has to know about.
 *
 * State is lifted to the form and written after the product saves; see
 * lib/productVariants.ts for the mapping onto groups and attachments.
 */

export function ProductVariantsSection({
  productId,
  productName,
  basePrice,
  categoryId,
  categoryName,
  state,
  onChange,
  onLoaded,
  formatCurrency,
  currencyPrefix,
  onePriceField,
  onOpenCosting,
}: {
  productId?: string
  productName: string
  basePrice: number
  categoryId: string | null | undefined
  categoryName?: string
  state: VariantsState
  onChange: (s: VariantsState) => void
  /** Called once with the server's state, so the form can set its dirty baseline. */
  onLoaded: (s: VariantsState) => void
  formatCurrency: (n: number) => string
  currencyPrefix: string
  /** The form's own price input, shown in "One price" mode. */
  onePriceField: ReactNode
  onOpenCosting?: () => void
}) {
  const { data: allGroups = [], isLoading: groupsLoading } = useQuery({
    queryKey: ['modifier-groups'],
    queryFn: async () => {
      const res = await apiClient.getModifierGroups()
      if (!res.success) throw new Error(res.message)
      return res.data ?? []
    },
  })
  const { data: usageCfg } = useQuery({
    queryKey: ['variant-usage'],
    queryFn: async () => {
      const res = await apiClient.getMenuModifiers()
      if (!res.success) throw new Error(res.message)
      return res.data
    },
  })
  const { data: attached, isLoading: attachedLoading } = useQuery({
    queryKey: ['product-modifier-groups', productId],
    enabled: Boolean(productId),
    queryFn: async () => {
      const res = await apiClient.getProductModifierGroups(productId as string)
      if (!res.success) throw new Error(res.message)
      return res.data
    },
  })
  // Which lists does the category push down? Read from the category, so the
  // list is right while creating a product that does not exist yet.
  const { data: categoryGroupIds = [] } = useQuery({
    queryKey: ['category-modifier-groups', categoryId],
    enabled: Boolean(categoryId),
    queryFn: async () => {
      const res = await apiClient.getCategoryModifierGroups(categoryId as string)
      if (!res.success) throw new Error(res.message)
      return res.data?.group_ids ?? []
    },
  })

  const byId = useMemo(() => new Map(allGroups.map((g) => [g.id, g])), [allGroups])
  const usage = useMemo(() => usageCounts(usageCfg), [usageCfg])

  // Seed from the server exactly once per product, after both reads land.
  const seeded = useRef<string | null>(null)
  useEffect(() => {
    if (groupsLoading) return
    const key = productId ?? 'new'
    if (seeded.current === key) return
    if (productId && (attachedLoading || !attached)) return
    if (!productId) {
      // New product: inherited lists come from the category alone.
      const resolved = categoryGroupIds
        .map((id) => byId.get(id))
        .filter((g): g is ModifierGroup => Boolean(g) && (g as ModifierGroup).is_active)
        .map((g) => ({ ...g, source: 'category' as const }))
      const s = stateFromServer(resolved, [], byId, usage)
      seeded.current = key
      onLoaded(s)
      return
    }
    const s = stateFromServer(attached!.groups, attached!.excluded_category_group_ids ?? [], byId, usage)
    seeded.current = key
    onLoaded(s)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupsLoading, attachedLoading, attached, productId, byId, categoryGroupIds])

  // ── derived rows ────────────────────────────────────────────────────────
  const inheritedIds = useMemo(
    () => categoryGroupIds.filter((id) => byId.get(id)?.is_active),
    [categoryGroupIds, byId],
  )
  const inheritedSizeId = inheritedIds.find((id) => { const g = byId.get(id); return g && isSizeGroup(g) }) ?? null
  const excludedQuestionRows = inheritedIds.filter((id) => {
    const g = byId.get(id)
    return g && !isSizeGroup(g) && state.excluded.includes(id)
  })

  const set = (patch: Partial<VariantsState>) => onChange({ ...state, ...patch })

  // ── price mode ──────────────────────────────────────────────────────────
  const bySize = state.sizes != null
  const setBySize = (on: boolean) => {
    if (on === bySize) return
    if (on) {
      // Prefer the category's size list if it exists and was turned off.
      if (inheritedSizeId && state.excluded.includes(inheritedSizeId)) {
        const g = byId.get(inheritedSizeId)!
        set({
          sizes: { groupId: g.id, source: 'category', draft: draftFromGroup(g), sharedCount: usage.get(g.id) ?? 1, dirty: false },
          excluded: state.excluded.filter((x) => x !== inheritedSizeId),
        })
        return
      }
      set({ sizes: newOwnSizes(productName) })
      return
    }
    const s = state.sizes!
    if (s.source === 'category' && s.groupId) {
      set({ sizes: null, excluded: [...state.excluded, s.groupId] })
    } else if (s.source === 'product' && s.groupId && s.sharedCount <= 1) {
      set({ sizes: null, deleteGroupIds: [...state.deleteGroupIds, s.groupId] })
    } else {
      set({ sizes: null })
    }
  }
  const makeOwnSizes = () => {
    const s = state.sizes
    if (!s) return
    const excluded = s.source === 'category' && s.groupId ? [...state.excluded, s.groupId] : state.excluded
    set({ sizes: newOwnSizes(productName, s.draft), excluded })
  }

  // ── questions ───────────────────────────────────────────────────────────
  const [query, setQuery] = useState('')
  const [suggestOpen, setSuggestOpen] = useState(false)
  const [editing, setEditing] = useState<string | null>(null) // groupId or tempKey

  const rowKey = (q: QuestionRow) => (q.kind === 'new' ? q.tempKey : q.groupId)
  const attachedIds = new Set(state.questions.filter((q): q is Extract<QuestionRow, { kind: 'shared' }> => q.kind === 'shared').map((q) => q.groupId))

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase()
    const shared = allGroups.filter(
      (g) => g.is_active && !isSizeGroup(g) && !attachedIds.has(g.id) && !inheritedIds.includes(g.id) &&
        (q === '' || g.name.toLowerCase().includes(q) || g.prompt.toLowerCase().includes(q)),
    )
    const templates = matchTemplates(query, ['one', 'many']).filter(
      (t) => !shared.some((g) => g.name.toLowerCase() === t.name.toLowerCase()),
    )
    return { shared, templates: templates.slice(0, 6) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, allGroups, state.questions, inheritedIds])

  const addShared = (g: ModifierGroup) => {
    set({ questions: [...state.questions, { kind: 'shared', groupId: g.id, source: 'product', dirty: false }] })
    setQuery(''); setSuggestOpen(false)
  }
  const addTemplate = (t: VariantTemplate) => {
    const key = nextTempKey()
    set({ questions: [...state.questions, { kind: 'new', tempKey: key, draft: draftFromTemplate(t) }] })
    setQuery(''); setSuggestOpen(false); setEditing(key)
  }
  const addNew = () => {
    const key = nextTempKey()
    const name = query.trim()
    const draft = { ...emptyDraft('one'), name, prompt: promptFor(name, 'one') }
    set({ questions: [...state.questions, { kind: 'new', tempKey: key, draft }] })
    setQuery(''); setSuggestOpen(false); setEditing(key)
  }
  const removeRow = (q: QuestionRow) => {
    if (q.kind === 'shared' && q.source === 'category') {
      set({ excluded: [...state.excluded, q.groupId], questions: state.questions.filter((x) => x !== q) })
    } else {
      set({ questions: state.questions.filter((x) => x !== q) })
    }
    if (editing === rowKey(q)) setEditing(null)
  }
  const restoreExcluded = (id: string) => {
    set({
      excluded: state.excluded.filter((x) => x !== id),
      questions: [...state.questions, { kind: 'shared', groupId: id, source: 'category', dirty: false }],
    })
  }
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir
    if (j < 0 || j >= state.questions.length) return
    const next = [...state.questions]
    ;[next[i], next[j]] = [next[j], next[i]]
    set({ questions: next })
  }
  const updateRow = (q: QuestionRow, draft: VariantDraft) =>
    set({
      questions: state.questions.map((x) =>
        x !== q ? x : x.kind === 'new' ? { ...x, draft } : { ...x, draft, dirty: true },
      ),
    })
  const openEdit = (q: QuestionRow) => {
    if (q.kind === 'shared' && !q.draft) {
      const g = byId.get(q.groupId)
      if (g) set({ questions: state.questions.map((x) => (x === q ? { ...x, draft: draftFromGroup(g) } : x)) })
    }
    setEditing(rowKey(q))
  }

  // ── preview ─────────────────────────────────────────────────────────────
  const previewQuestions: PreviewQuestion[] = []
  if (state.sizes) {
    previewQuestions.push({
      key: 'sizes', prompt: state.sizes.draft.prompt || 'Which size?', kind: 'size', rule: state.sizes.draft.rule,
      choices: state.sizes.draft.choices, focus: editing === 'sizes',
    })
  }
  for (const q of state.questions) {
    const draft = q.kind === 'new' ? q.draft : q.draft
    const g = q.kind === 'shared' ? byId.get(q.groupId) : undefined
    if (draft) {
      previewQuestions.push({ key: rowKey(q), prompt: draft.prompt, kind: draft.kind, rule: draft.rule, choices: draft.choices, focus: editing === rowKey(q) })
    } else if (g) {
      previewQuestions.push({
        key: g.id, prompt: g.prompt, kind: kindOf(g), rule: ruleOf(g),
        choices: (g.options ?? []).map((o) => ({ name: o.name, price: o.price_delta, isDefault: o.is_default, isAvailable: o.is_available })),
        muted: q.kind === 'shared' && q.source === 'category',
      })
    }
  }

  const otherGroups = (exceptId?: string) => allGroups.filter((g) => g.id !== exceptId)
  // Usage can land after the state was seeded, so read it live.
  const sizesSharedCount = state.sizes
    ? Math.max(state.sizes.sharedCount, state.sizes.groupId ? (usage.get(state.sizes.groupId) ?? 0) : 0)
    : 0
  const sizesShared = sizesSharedCount > 1

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_15rem]">
      <div className="space-y-6">
        {/* ── Price ─────────────────────────────────────────────────────── */}
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold">Price</p>
            <div className="inline-flex overflow-hidden rounded-lg border text-sm" role="group" aria-label="Price mode">
              <button type="button" aria-pressed={!bySize} onClick={() => setBySize(false)}
                className={cn('px-3 py-1.5', !bySize ? 'bg-primary/10 font-medium text-primary' : 'text-muted-foreground hover:bg-muted')}>
                One price
              </button>
              <button type="button" aria-pressed={bySize} onClick={() => setBySize(true)}
                className={cn('border-l px-3 py-1.5', bySize ? 'bg-primary/10 font-medium text-primary' : 'text-muted-foreground hover:bg-muted')}>
                By size
              </button>
            </div>
          </div>

          {!bySize ? (
            <div className="max-w-xs">{onePriceField}</div>
          ) : state.sizes ? (
            <div className="space-y-2" onFocusCapture={() => setEditing('sizes')}>
              {sizesShared && (
                <p className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs">
                  <span>
                    <strong>{state.sizes.draft.name}</strong> is shared with {sizesSharedCount - 1} other item{sizesSharedCount === 2 ? '' : 's'}
                    {state.sizes.source === 'category' ? ` through the ${categoryName ?? 'category'} category` : ''}. Changing prices here changes them everywhere.
                  </span>
                  <Button type="button" size="sm" variant="outline" className="h-7" onClick={makeOwnSizes}>
                    Use separate sizes for this item
                  </Button>
                </p>
              )}
              <VariantListEditor
                draft={state.sizes.draft}
                onChange={(d) => set({ sizes: { ...state.sizes!, draft: d, dirty: true } })}
                otherGroups={otherGroups(state.sizes.groupId)}
                currencyPrefix={currencyPrefix}
                lockKind
                compact
                ingredientsNote={<CostingNote onOpenCosting={onOpenCosting} />}
              />
              <p className="text-xs text-muted-foreground">
                The till asks “{state.sizes.draft.prompt || 'Which size?'}” and the chosen price is the item price. The menu lists the lowest.
              </p>
            </div>
          ) : null}
        </div>

        {/* ── Questions ─────────────────────────────────────────────────── */}
        <div className="space-y-3 border-t pt-5">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm font-semibold">What the cashier is asked</p>
            <p className="text-xs text-muted-foreground">In this order{bySize ? ', after size' : ''}</p>
          </div>

          {groupsLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <div className="space-y-2">
              {state.questions.map((q, i) => {
                const key = rowKey(q)
                const g = q.kind === 'shared' ? byId.get(q.groupId) : undefined
                const draft = q.kind === 'new' ? q.draft : q.draft
                const name = draft?.name || g?.name || 'New question'
                const prompt = draft?.prompt || g?.prompt || ''
                const kind = draft?.kind ?? (g ? kindOf(g) : 'one')
                const rule = draft?.rule ?? (g ? ruleOf(g) : { mode: 'optional' as const, n: 0, m: 1 })
                const count = q.kind === 'shared' ? (usage.get(q.groupId) ?? 0) : 0
                const shared = count > 1
                const inherited = q.kind === 'shared' && q.source === 'category'
                const isOpen = editing === key
                const problem = draft ? draftProblem(draft) : null
                const summary = draft
                  ? draft.choices.filter((c) => c.name.trim()).map((c) => (c.price > 0 ? `${c.name} +${formatCurrency(c.price)}` : c.name)).slice(0, 4).join(' · ')
                  : g ? choicesSummary(g, formatCurrency) : ''
                return (
                  <div key={key} className={cn('rounded-lg border', shared && 'border-l-2 border-l-amber-500', isOpen && 'border-primary')}>
                    <div className="flex items-center gap-2 px-2 py-2">
                      <div className="flex flex-col text-muted-foreground/60">
                        <button type="button" aria-label={`Move ${name} up`} disabled={i === 0} onClick={() => move(i, -1)} className="disabled:opacity-25"><ChevronUp className="h-3 w-3" /></button>
                        <button type="button" aria-label={`Move ${name} down`} disabled={i === state.questions.length - 1} onClick={() => move(i, 1)} className="disabled:opacity-25"><ChevronDown className="h-3 w-3" /></button>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5 text-sm">
                          <span className="font-medium">{prompt || name}</span>
                          <Badge variant={isRequired(rule) ? 'secondary' : 'outline'} className="text-[10px]">{rulePill(kind, rule)}</Badge>
                          {shared && <Badge variant="outline" className="border-amber-500/50 text-[10px] text-amber-700 dark:text-amber-400">shared · {count} items</Badge>}
                          {inherited && <Badge variant="outline" className="text-[10px]">from {categoryName ?? 'category'}</Badge>}
                          {q.kind === 'new' && <Badge variant="outline" className="text-[10px]">new</Badge>}
                          {problem && isOpen === false && <Badge variant="destructive" className="text-[10px]">{problem}</Badge>}
                        </div>
                        {summary && <p className="truncate text-xs text-muted-foreground">{summary}</p>}
                      </div>
                      {!isOpen && (
                        <Button type="button" size="sm" variant="ghost" className="h-7 gap-1 px-2" onClick={() => openEdit(q)}>
                          <Pencil className="h-3 w-3" /> Edit
                        </Button>
                      )}
                      <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" aria-label={inherited ? `Don't ask ${name} on this item` : `Remove ${name}`} onClick={() => removeRow(q)}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    {isOpen && draft && (
                      <div className="border-t p-3">
                        <VariantListEditor
                          draft={draft}
                          onChange={(d) => updateRow(q, d)}
                          otherGroups={otherGroups(draft.id)}
                          currencyPrefix={currencyPrefix}
                          sharedCount={count}
                          ingredientsNote={<CostingNote onOpenCosting={onOpenCosting} />}
                        />
                        <div className="mt-3 flex items-center justify-between gap-2">
                          <span className="text-xs text-muted-foreground">{problem ?? 'Saved with the product.'}</span>
                          <Button type="button" size="sm" variant="outline" onClick={() => setEditing(null)}>Done</Button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}

              {excludedQuestionRows.map((id) => {
                const g = byId.get(id)!
                return (
                  <label key={id} className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed px-3 py-2 text-muted-foreground">
                    <Switch checked={false} onCheckedChange={() => restoreExcluded(id)} aria-label={`Ask ${g.name} on this item`} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{g.prompt || g.name}</span>
                      <span className="block truncate text-xs">From the {categoryName ?? 'category'} category, not asked for this item</span>
                    </span>
                  </label>
                )
              })}

              {/* Type-to-add */}
              <div className="relative">
                <input
                  type="text"
                  value={query}
                  placeholder="Add a question… e.g. Milk, Sweetness, Extras"
                  aria-label="Add a question"
                  autoComplete="off"
                  className="h-9 w-full rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none"
                  onChange={(e) => { setQuery(e.target.value); setSuggestOpen(true) }}
                  onFocus={() => setSuggestOpen(true)}
                  onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); if (suggestions.shared[0]) addShared(suggestions.shared[0]); else addNew() }
                    if (e.key === 'Escape') setSuggestOpen(false)
                  }}
                />
                {suggestOpen && (
                  <div className="absolute left-0 right-0 top-10 z-20 overflow-hidden rounded-lg border bg-popover shadow-md">
                    {suggestions.shared.map((g) => (
                      <button key={g.id} type="button" onMouseDown={(e) => { e.preventDefault(); addShared(g) }}
                        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-muted">
                        <span className="min-w-0 truncate"><span className="font-medium">{g.name}</span> <span className="text-muted-foreground">{choicesSummary(g, formatCurrency, 3)}</span></span>
                        <span className="shrink-0 text-xs text-muted-foreground">{(usage.get(g.id) ?? 0) > 0 ? `used on ${usage.get(g.id)} items` : 'not used yet'}</span>
                      </button>
                    ))}
                    {suggestions.templates.map((t) => (
                      <button key={t.key} type="button" onMouseDown={(e) => { e.preventDefault(); addTemplate(t) }}
                        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-muted">
                        <span className="min-w-0 truncate"><span className="font-medium">{t.name}</span> <span className="text-muted-foreground">{t.choices.map((c) => c.name).slice(0, 4).join(', ')}</span></span>
                        <span className="shrink-0 text-xs text-muted-foreground">template</span>
                      </button>
                    ))}
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); addNew() }}
                      className="flex w-full items-center gap-2 border-t px-3 py-2 text-left text-sm font-medium text-primary hover:bg-muted">
                      + New question{query.trim() ? ` “${query.trim()}”` : ''} for this item
                    </button>
                  </div>
                )}
              </div>

              <p className="text-xs text-muted-foreground">
                {state.questions.length === 0 && !bySize
                  ? 'Nothing asked. A single tap adds this item to the order.'
                  : 'Shared lists carry an orange edge. Editing one changes every item that asks it; you are told how many.'}
              </p>
            </div>
          )}
        </div>
      </div>

      <aside className="space-y-2 lg:sticky lg:top-4 lg:self-start">
        <p className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Cashier’s view <span className="font-mono text-[10px] text-primary">● live</span>
        </p>
        <CashierPreview
          productName={productName}
          basePrice={basePrice}
          questions={previewQuestions}
          formatCurrency={formatCurrency}
          footnote={previewQuestions.length ? 'Tap a choice to try the rule. The price moves the way it will on the till.' : undefined}
        />
      </aside>
    </div>
  )
}

function CostingNote({ onOpenCosting }: { onOpenCosting?: () => void }) {
  return (
    <span>
      What each choice adds to or replaces in the recipe is set under Costing &amp; Recipes
      {onOpenCosting ? (
        <>
          {' '}·{' '}
          <button type="button" className="font-medium text-primary underline-offset-2 hover:underline" onClick={onOpenCosting}>
            open it
          </button>
        </>
      ) : ' once the item is saved'}
      .
    </span>
  )
}

